import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    /*
     * ECHAP DOIT FONCTIONNER MEME QUAND LE FOCUS N'EST PAS DANS LE TIROIR.
     *
     * L'ecouteur ci-dessus est pose sur le composant : il ne recoit la touche que si
     * le focus se trouve a l'interieur. Or `trapFocus` n'est appele qu'a la fin de
     * l'animation d'ouverture, et mesure faite, le focus restait sur `<body>` — Echap
     * n'atteignait donc jamais le tiroir, ni sur mobile ni sur ordinateur. On ecoute
     * aussi au niveau du document tant que le tiroir est ouvert, comme le fait deja
     * `dialog.js` pour les fenetres du theme.
     */
    document.addEventListener('keydown', this.#onDocumentKeyDown);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    document.removeEventListener('keydown', this.#onDocumentKeyDown);
  }

  /**
   * Ferme le tiroir — ou le sous-menu ouvert — quand Echap est presse, ou que soit
   * le focus. Un `<input>` ou un `<textarea>` garde la priorite : Echap y sert
   * d'abord a annuler la saisie ou a refermer une liste de suggestions.
   * @param {KeyboardEvent} event
   */
  #onDocumentKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;

    const target = event.target;
    if (target instanceof HTMLElement && target.closest('input, textarea, select, [contenteditable]')) return;

    /* Le focus est deja dans le tiroir : `#onKeyUp` fera le travail, et fermera le
       sous-menu le plus proche plutot que le tiroir entier. */
    if (target instanceof Node && this.contains(target)) return;

    this.#close(this.refs.details);
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      /*
       * LE PIEGE A FOCUS N'ATTEND PAS LA FIN DU GLISSEMENT.
       *
       * Il etait pose dans `onAnimationEnd`, mais le tiroir s'ouvre par une TRANSITION
       * CSS, pas par une animation : `animationend` ne partait jamais et `trapFocus`
       * n'etait donc jamais appele. Mesure sur la vitrine : 1,2 s apres l'ouverture, le
       * focus etait encore sur le bouton d'ouverture, hors du panneau — la navigation au
       * clavier continuait derriere lui.
       *
       * Le piege n'a aucune raison d'attendre : il pose ses ecouteurs, il n'anime rien.
       *
       * MAIS IL NE PLACE PAS LE FOCUS ICI. `trapFocus` se termine par
       * `container.focus()` — or le conteneur est un `<details>`, qui n'est pas
       * focusable sans `tabindex` : l'appel est sans effet et le focus reste ou il
       * etait. On vise donc explicitement le premier element utile DU PANNEAU, une
       * fois celui-ci reellement affiche : le tiroir glisse par transition, on attend
       * donc `transitionend`, avec un delai de secours si la transition est coupee
       * (mouvement reduit, onglet en arriere-plan).
       */
      trapFocus(details);

      const panel = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      const placeFocus = () => {
        if (!details.classList.contains('menu-open')) return;
        if (details.contains(document.activeElement) && document.activeElement !== summary) return;
        /* Le premier element focusable du panneau est un bouton de 0x0 (commande
           masquee du menu) : `focus()` y est sans effet. On prend donc le premier
           element REELLEMENT AFFICHE — mesure : sans ce filtre, le focus restait sur
           le bouton d'ouverture. */
        const candidates = (panel ?? details).querySelectorAll(
          'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), input:not([type=hidden]):enabled'
        );
        for (const candidate of candidates) {
          if (!(candidate instanceof HTMLElement)) continue;
          if (candidate.offsetParent === null) continue;
          const box = candidate.getBoundingClientRect();
          if (box.width < 1 || box.height < 1) continue;
          candidate.focus();
          break;
        }
      };

      if (panel) {
        panel.addEventListener('transitionend', placeFocus, { once: true });
      }
      setTimeout(placeFocus, 400);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
